import chroma from "chroma-js";

// Legacy screens supply literal colors instead of palette tokens. Adapt those at
// the theme boundary so nested sx selectors and portalled dialogs behave alike.
// Keep documents, images, charts and the light theme outside this compatibility layer.
const cache = new Map();
export function modeColor(value, role, palette) {
  if (palette.mode !== "dark" || typeof value !== "string") return value;
  const important = /\s*!important\s*$/.test(value) ? " !important" : "";
  const input = value.replace(/\s*!important\s*$/, "");
  if (!chroma.valid(input)) return value;
  const key = `${palette.background.paper}:${role}:${value}`;
  if (cache.has(key)) return cache.get(key);
  const original = chroma(input);
  let result = original;
  // Transparent overlays must retain their opacity and the surface underneath.
  if (original.alpha() >= 0.4) {
    if (role === "background" && original.luminance() > 0.08) {
      result = chroma.mix(
        original,
        palette.background.paper,
        original.luminance() > 0.7 ? 0.97 : 0.88,
        "rgb"
      );
    } else if (role === "foreground" && original.luminance() < 0.5) {
      result = original.luminance(0.65);
    } else if (role === "border") {
      result = original.luminance(0.2);
    }
  }
  const output = result.alpha(original.alpha()).css() + important;
  if (cache.size > 2000) cache.clear();
  cache.set(key, output);
  return output;
}

export function modeStyle(style, palette) {
  if (!style || palette.mode !== "dark") return style;
  return Object.fromEntries(
    Object.entries(style).map(([property, value]) => {
      if (["color", "WebkitTextFillColor", "fill", "stroke"].includes(property)) {
        return [property, modeColor(value, "foreground", palette)];
      }
      if (["background", "backgroundColor", "backgroundImage"].includes(property)) {
        return [property, modeBackground(value, palette)];
      }
      if (/^(border|outline)/.test(property) && typeof value === "string") {
        return [property, modeBorder(value, palette)];
      }
      return [property, value];
    })
  );
}

function modeBackground(value, palette) {
  if (typeof value === "string" && /^(repeating-)?(linear|radial|conic)-gradient\(/.test(value)) {
    return value.replace(/#[\da-f]{3,8}\b|rgba?\([^)]*\)|\b(?:white|black)\b/gi, (color) =>
      modeColor(color, "background", palette)
    );
  }
  return modeColor(value, "background", palette);
}

function modeBorder(value, palette) {
  // Only color tokens: never rewrite widths, radius, URLs or CSS variables.
  if (typeof value !== "string") return value;
  return value.replace(/#[\da-f]{3,8}\b|rgba?\([^)]*\)|\b(?:white|black)\b/gi, (color) =>
    modeColor(color, "border", palette)
  );
}

export function createColorModeSxConfig(palette) {
  const transform = (role) => (value, original) => {
    // Semantic tokens already belong to this theme; only adapt legacy literals.
    if (typeof original === "string" && /^(text|background|action)\./.test(original)) return value;
    return role === "background" ? modeBackground(value, palette) : modeColor(value, role, palette);
  };
  const config = {
    color: { themeKey: "palette", transform: transform("foreground") },
    bgcolor: {
      themeKey: "palette",
      cssProperty: "backgroundColor",
      transform: transform("background"),
    },
    backgroundColor: { themeKey: "palette", transform: transform("background") },
    background: { transform: transform("background") },
    backgroundImage: { transform: transform("background") },
  };
  ["border", "borderTop", "borderBottom", "borderLeft", "borderRight", "outline"].forEach(
    (property) => {
      config[property] = {
        themeKey: "borders",
        transform: (value) =>
          typeof value === "number" ? `${value}px solid` : modeBorder(value, palette),
      };
      config[`${property}Color`] = { themeKey: "palette", transform: transform("border") };
    }
  );
  return config;
}

// Styled shared components and theme overrides also contain nested hover/focus rules.
export function modeStyles(styles, palette) {
  if (!styles || palette.mode !== "dark" || typeof styles !== "object") return styles;
  if (Array.isArray(styles)) return styles.map((style) => modeStyles(style, palette));
  const result = modeStyle(styles, palette);
  Object.keys(result).forEach((key) => {
    if (result[key] && typeof result[key] === "object") {
      result[key] = modeStyles(result[key], palette);
    }
  });
  return result;
}

export function colorModeComponents(components, palette) {
  return Object.fromEntries(
    Object.entries(components).map(([name, component]) => [
      name,
      { ...component, styleOverrides: modeStyles(component.styleOverrides, palette) },
    ])
  );
}
